import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'

import downloadAppQrcodeIcon from '@assets/download-app-qrcode.svg'
import { Button, ButtonClean, PriceString, ProductsList, Tag } from '@fourthwall/components'
import { MOBILE_APP_ANDROID_LINK, MOBILE_APP_IOS_LINK } from '@fourthwall/utils/dist/links'
import { reverse, routing } from '@utils/routing'

import type { ThankYou } from '../../../../ThankYous.types'
import AndroidIcon from '../../assets/android.svg'
import iOSIcon from '../../assets/iOS.svg'
import * as S from './Sidebar.styled'

type SidebarProps = {
  thankYou: ThankYou
}

export const Sidebar = ({ thankYou }: SidebarProps) => {
  const scrollableContentRef = useRef<HTMLDivElement>(null)
  const [scrollableContentHeight, setScrollableContentHeight] = useState<number | undefined>(
    undefined,
  )

  useEffect(() => {
    if (scrollableContentRef.current) {
      const rect = scrollableContentRef.current.getBoundingClientRect()
      setScrollableContentHeight(rect.height)
    }
  }, [])

  const getTitle = () => {
    if (thankYou.state === 'SENT' && thankYou.thankYous[0]) {
      return `Thank you sent on ${dayjs(thankYou.updatedAt).format('MMM D, YYYY')} to`
    }

    return 'Send thank you to'
  }

  const getLink = () => {
    switch (thankYou.contributionId.type) {
      case 'ORDER':
        return reverse(routing.contributions.orders.order.self, {
          orderId: thankYou.contributionId.orderId,
        })
      case 'DONATION':
        return reverse(routing.contributions.orders.donations.donation.self, {
          donationId: thankYou.contributionId.donationId,
        })
      case 'GIVEAWAY':
        return reverse(routing.contributions.orders.giveaways.giveaway, {
          giveawayId: thankYou.contributionId.giveawayId,
        })
    }
  }

  const getDownloadLabel = () => {
    if (thankYou.thankYous[0]?.mediaType.type === 'image') {
      return 'Download photo'
    }

    return 'Download recording'
  }

  const renderTags = () => {
    if (!thankYou.tags.length) {
      return null
    }

    return (
      <S.TagsContainer>
        {thankYou.tags.map((tag, index) => (
          <Tag key={index} label={tag} size="small" display="inline-flex" />
        ))}
      </S.TagsContainer>
    )
  }

  const renderProducts = () => {
    if (!thankYou.offers.length && !thankYou.donation) {
      return null
    }

    return (
      <ProductsList>
        {thankYou.offers.map((offer, index) => (
          <ProductsList.Item
            key={index}
            image={offer.thumbnailImageUrl}
            name={offer.title}
            size="small"
          />
        ))}
      </ProductsList>
    )
  }

  return (
    <S.Container>
      <S.Content>
        <S.Section>
          <S.SectionHeader>{getTitle()}</S.SectionHeader>
          <S.Header>{thankYou.supporter.fullName}</S.Header>

          {thankYou.state === 'SENT' && thankYou.thankYous[0] && (
            <S.ButtonContainer>
              <Button
                label={getDownloadLabel()}
                href={thankYou.thankYous[0].mediaUrl}
                download={`${thankYou.thankYous[0].id}.${thankYou.thankYous[0].mediaType.subtype}`}
                target="_blank"
                fullWidth
              />
            </S.ButtonContainer>
          )}
        </S.Section>

        <S.ScrollableContentContainer ref={scrollableContentRef}>
          {!!scrollableContentHeight && (
            <S.ScrollableContent maxHeight={scrollableContentHeight}>
              <S.Section>
                <S.SectionHeader>Message left</S.SectionHeader>
                <S.Message>{thankYou.message ? `"${thankYou.message}"` : '-'}</S.Message>
              </S.Section>

              <S.Section>
                <S.SectionHeader>
                  Placed order for <PriceString {...thankYou.total} />
                  <S.DetailsButtonContainer>
                    <ButtonClean
                      label="View details"
                      appearance="secondary"
                      to={getLink()}
                      target="_blank"
                    />
                  </S.DetailsButtonContainer>
                </S.SectionHeader>

                {renderTags()}

                {renderProducts()}
              </S.Section>
            </S.ScrollableContent>
          )}
        </S.ScrollableContentContainer>
      </S.Content>

      <S.Footer>
        <S.DownloadAppQrcodeIcon src={downloadAppQrcodeIcon} alt="Download app" />

        <div>
          You can also send thank you notes using our app for{' '}
          <S.PlatformLink href={MOBILE_APP_IOS_LINK} target="_blank" rel="noreferrer">
            <S.PlatformIcon src={iOSIcon} alt="iOS" />
            iOS
          </S.PlatformLink>{' '}
          or{' '}
          <S.PlatformLink href={MOBILE_APP_ANDROID_LINK} target="_blank" rel="noreferrer">
            <S.PlatformIcon src={AndroidIcon} alt="iOS" />
            Android
          </S.PlatformLink>
          .
        </div>
      </S.Footer>
    </S.Container>
  )
}
